import React, { useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import { Empty } from "antd";
import { formatWithCommas } from "../../../assets/helpers";
import "./StatusBreakdown.scss";

/**
 * Case-status donut with an interactive legend list. The centre reacts to
 * hover, falling back to the overall total when nothing is highlighted.
 */
/* 0.6% became "1%" when the share was derived here and rounded to a whole
   number. Where the API already reports the split, its figures are used as
   they are; the derived fallback is only for callers that send none. */
const formatPercent = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "0%";
  return `${Number.isInteger(number) ? number : Number(number.toFixed(1))}%`;
};

const StatusBreakdown = ({
  data = [],
  labels = [],
  colors = [],
  centerLabel = "Total",
  height = 168,
  isPercentage = false,
  valueFormatter,
  /* one entry per slice, aligned with `data`; a null or missing entry falls
     back to the share computed from the plotted values */
  percentages = [],
}) => {
  const [active, setActive] = useState(null);

  const total = useMemo(
    () => data.reduce((a, b) => a + (Number(b) || 0), 0),
    [data],
  );

  const option = useMemo(
    () => ({
      tooltip: { show: false },
      legend: { show: false },
      series: [
        {
          type: "pie",
          radius: ["70%", "94%"],
          center: ["50%", "50%"],
          padAngle: 2,
          itemStyle: { borderRadius: 8 },
          label: { show: false },
          labelLine: { show: false },
          emphasis: { scale: true, scaleSize: 7 },
          data: labels.map((l, i) => ({
            value: data[i] || 0,
            name: l,
            itemStyle: { color: colors[i] },
          })),
        },
      ],
    }),
    [data, labels, colors],
  );

  const events = useMemo(
    () => ({
      mouseover: (p) => setActive(p.dataIndex),
      mouseout: () => setActive(null),
    }),
    [],
  );

  if (!total) {
    return (
      <div
        className="dsh-status dsh-status--empty"
        style={{ minHeight: height }}
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No case data available"
        />
      </div>
    );
  }

  const fmt =
    valueFormatter ||
    ((v) => (isPercentage ? `${v}%` : formatWithCommas(v)));

  const shareOf = (index) => {
    const given = percentages[index];
    if (given !== undefined && given !== null && given !== "") return given;
    return total ? ((data[index] || 0) / total) * 100 : 0;
  };

  /* A ring is the wrong shape for a two-way split that runs lopsided: at
     0.6% against 99.4% it draws an all-but-complete circle with a hairline
     gap, which reads as "one category" and communicates nothing. Two
     categories are a rate, so they are shown as one - a headline share, a
     bar, and the pair of figures behind it. Three or more slices keep the
     ring, where a part-to-whole comparison is what the shape is for. */
  if (labels.length === 2) {
    const leadShare = shareOf(0);

    return (
      <div className="dsh-status dsh-status--rate">
        <div className="dsh-status__rate">
          <span className="dsh-status__rate-label">{labels[0]} rate</span>
          <b className="dsh-status__rate-value">{formatPercent(leadShare)}</b>
          <span className="dsh-status__rate-meter">
            <i
              style={{
                width: `${Math.max(Math.min(Number(leadShare) || 0, 100), 0)}%`,
                background: colors[0],
              }}
            />
          </span>
          <span className="dsh-status__rate-sub">
            {fmt(data[0] || 0)} of {fmt(total)} {centerLabel.toLowerCase()}
          </span>
        </div>

        <div className="dsh-status__cards">
          {labels.map((label, i) => (
            <div
              key={label}
              className={`dsh-status__card ${active === i ? "is-active" : ""}`}
              onMouseEnter={() => setActive(i)}
              onMouseLeave={() => setActive(null)}
            >
              <span className="dsh-status__card-head">
                <span
                  className="dsh-status__dot"
                  style={{ background: colors[i] }}
                />
                {label}
              </span>
              <b className="dsh-status__card-value">{fmt(data[i] || 0)}</b>
              <span className="dsh-status__card-pct">
                {formatPercent(shareOf(i))}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const activeValue = active != null ? data[active] || 0 : total;
  const activeName = active != null ? labels[active] : centerLabel;

  return (
    <div className="dsh-status">
      <div className="dsh-status__chart" style={{ height, width: height }}>
        <ReactECharts
          option={option}
          onEvents={events}
          style={{ height, width: height }}
          opts={{ renderer: "svg" }}
        />
        <div className="dsh-status__center">
          <b>{fmt(activeValue)}</b>
          <span>{activeName}</span>
        </div>
      </div>

      <ul className="dsh-status__legend">
        {labels.map((label, i) => {
          const value = data[i] || 0;
          const pct = shareOf(i);
          return (
            <li
              key={label}
              className={active === i ? "is-active" : ""}
              onMouseEnter={() => setActive(i)}
              onMouseLeave={() => setActive(null)}
            >
              <span className="dsh-status__dot" style={{ background: colors[i] }} />
              <span className="dsh-status__name">{label}</span>
              <span className="dsh-status__value">{fmt(value)}</span>
              <span className="dsh-status__pct">{formatPercent(pct)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default StatusBreakdown;
